import { Component, EventEmitter, Input, Output } from '@angular/core';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { AddToBasketModalComponent } from 'src/app/components/modal-components/add-to-basket-modal/add-to-basket-modal.component';
import { Article } from 'src/app/interfaces/article';
import { ApplicationService } from 'src/app/services/application.service';
import { ArticleService } from 'src/app/services/article.service';
import { BasketService } from 'src/app/services/basket.service';
import { FavoritesService } from 'src/app/services/favorites.service';

@Component({
    selector: 'app-article-table-view',
    templateUrl: './article-table-view.component.html',
    styleUrls: ['./article-table-view.component.scss'],
    standalone: false
})
export class ArticleTableViewComponent {
  private static readonly STORAGE_KEY = 'articleTableView_activeArticleId';

  private _articles: Article[] = [];

  @Input() set articles(value: Article[]) {
    this._articles = value;
    const storedId = sessionStorage.getItem(ArticleTableViewComponent.STORAGE_KEY);
    if (storedId) {
      this.activeArticle = value?.find(a => String(a.id) === storedId) ?? null;
    }
  }
  get articles(): Article[] { return this._articles; }

  @Input() companyGroupCode: string = '';
  @Output() articleClick: EventEmitter<Article> = new EventEmitter<Article>();

  activeArticle: Article | null = null;

  // Table columns configuration
  displayedColumns: string[] = [
    'image',
    'article_code',
    'description',
    'brand',
    'category',
    'price',
    'stock',
    'availability',
    'favorite',
    'actions'
  ];

  constructor(
    private router: Router,
    private applicationService: ApplicationService,
    private articleService: ArticleService,
    private favoritesService: FavoritesService,
    private basketService: BasketService,
    private modalService: NgbModal
  ) {}

  /**
   * Navigate to article details
   */
  viewArticleDetails(article: Article): void {
    this.activeArticle = article;
    sessionStorage.setItem(ArticleTableViewComponent.STORAGE_KEY, String(article.id));
    this.articleClick.emit(article);
  }

  /**
   * Add article to basket
   */
  onAddToBasket(article: Article, event: Event): void {
    event.stopPropagation();
    
    if (!article.authorized_to_place_in_basket) {
      return;
    }

    // Open the add to basket modal
    const modalRef = this.modalService.open(AddToBasketModalComponent, {
      centered: true
    });
    
    modalRef.componentInstance.article = article;
    modalRef.componentInstance.companyGroupCode = this.companyGroupCode || this.applicationService.getSelectCompanyGroupCode();
  }

  /**
   * Toggle favorite status
   */
  toggleFavorite(article: Article, event: Event): void {
    event.stopPropagation();
    if (article.is_favorite) {
      this.favoritesService.removeFavorite(article.single_article_code);
    } else {
      this.favoritesService.addFavorite(article.single_article_code);
    }
    article.is_favorite = !article.is_favorite;
  }

  /**
   * Check if article is favorite
   */
  isFavorite(article: Article): boolean {
    return article.is_favorite || false;
  }

  /**
   * Get article image URL
   */
  getArticleImage(article: Article): string {
    if (article.images && article.images.length > 0) {
      return article.images[0];
    }
    if (article.thumbnail) {
      return article.thumbnail;
    }
    return 'assets/images/placeholder.png';
  }

  /**
   * Get formatted price
   */
  getPrice(article: Article): string {
    // This is just for fallback display, actual price component will handle currency conversion
    if (article.net_price && article.net_price.value) {
      const currency = article.net_price.currency || 'EUR';
      const symbol = currency === 'EUR' ? '€' : currency;
      return `${symbol} ${article.net_price.value.toFixed(2)}`;
    }
    if (article.gross_price && article.gross_price.value) {
      const currency = article.gross_price.currency || 'EUR';
      const symbol = currency === 'EUR' ? '€' : currency;
      return `${symbol} ${article.gross_price.value.toFixed(2)}`;
    }
    return '-';
  }

  /**
   * Get stock status
   */
  getStockStatus(article: Article): string {
    if (article.stock > 0) {
      return `${article.stock}`;
    }
    return '0';
  }

  /**
   * Get availability class
   */
  getAvailabilityClass(article: Article): string {
    if (article.stock > 0) {
      return 'text-success';
    }
    if (article.delivery_week || article.stock_week) {
      return 'text-warning';
    }
    return 'text-danger';
  }

  /**
   * Get category name
   */
  getCategory(article: Article): string {
    return article.classification_codes.join(', ');
  }

  /**
   * Track by function for performance
   */
  trackByArticleId(index: number, article: Article): any {
    return article.id;
  }
}
